import React from 'react';
import s from './s.module.less';
import Icon from './icon.png';
import { Button, Divider, message, Popover, Switch } from 'antd';
import { disconnetFromGoogle } from 'api/provider';
import useGoogleAuthStatus from 'hooks/useGoogleAuthStatus';
import { ReminderSettings } from 'types/calendarSetting';
import { updateReminderSettings } from 'api/calendarSetting';

interface Props {
    onUpdate: () => void;
    value?: ReminderSettings,
}

const BookingRestrictions = (props: Props) => {
    const { onUpdate, value } = props;
    const [sameDayCheck, setSameDayCheck] = React.useState(false);
    const [nextDayCheck, setNextDayCheck] = React.useState(false);
    const [loadingSame, setLoadingSame] = React.useState(false);
    const [loadingNext, setLoadingNext] = React.useState(false);

    React.useEffect(() => {
        setSameDayCheck(!!value?.blockSameDayBooking);
        setNextDayCheck(!!value?.blockNextDayBooking);
    }, [value]);

    const handleChangeSameDay = async (check: boolean) => {
        const data = {
            blockSameDayBooking: check,
        }
        setLoadingSame(true);
        const result = await updateReminderSettings(data);
        setLoadingSame(false);
        if (!result.error) {
            if (check) {
                message.success("Booking restrictions updated. Patients can’t book on the same calendar day.")
            } else {
                message.success("Booking restrictions updated. Patients can now book on the same calendar day.")
            }
            onUpdate();
        }
    }
    const handleChangeNextDay = async (check: boolean) => {
        const data: any = {
            blockNextDayBooking: check,
        }
        if (check) {
            data.blockSameDayBooking = true;
        }
        setLoadingNext(true);
        const result = await updateReminderSettings(data);
        setLoadingNext(false);
        if (!result.error) {
            if (check) {
                message.success("Booking restrictions updated. Patients can’t book on the same or next calendar day.")
            } else {
                message.success("Booking restrictions updated. Patients can now book for the next calendar day.")
            }
            onUpdate();
        }
    }

    return (
        <div className={s.wrap}>
            <div className="text-gray-800 text-xl font-medium">Booking restrictions</div>
            <Divider />
            <div className={s.tips}>Prevent patients from booking appointments too close to the current time</div>
            <div className={s.config}>
                <Switch
                    checked={sameDayCheck}
                    onChange={(check) => {
                        handleChangeSameDay(check)
                    }}
                    disabled={nextDayCheck}
                    loading={loadingSame}
                ></Switch>
                <span className={s.label}>
                    Block same-day bookings
                </span>
                <Popover
                    placement='rightTop'
                    content={
                        <div>
                            <p>Patients won’t be able to book </p>
                            <p>appointments for the same day.</p>
                        </div>
                    }
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                        <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </Popover>
            </div>
            <div className={s.config}>
                <Switch
                    checked={nextDayCheck}
                    onChange={(check) => {
                        handleChangeNextDay(check)
                    }}
                    loading={loadingNext}
                ></Switch>
                <span className={s.label}>
                    Block next-day bookings
                </span>
                <Popover
                    placement='rightTop'
                    content={
                        <div>
                            <p>Patients won’t be able to book </p>
                            <p>appointments for the next calendar day.</p>
                        </div>
                    }
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="21" height="20" viewBox="0 0 21 20" fill="none">
                        <path d="M7.1992 7.5C7.67968 6.52901 8.97612 5.83333 10.5 5.83333C12.433 5.83333 14 6.95262 14 8.33333C14 9.49953 12.8821 10.4792 11.37 10.7555C10.8954 10.8422 10.5 11.2064 10.5 11.6667M10.5 14.1667H10.5088M18.375 10C18.375 14.1421 14.8492 17.5 10.5 17.5C6.15076 17.5 2.625 14.1421 2.625 10C2.625 5.85786 6.15076 2.5 10.5 2.5C14.8492 2.5 18.375 5.85786 18.375 10Z" stroke="#6B7280" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                </Popover>
            </div>
        </div>
    );
};

export default BookingRestrictions;
